import { getTypedLowerCase } from "utils/service";
import { ReferenceGroup, ReferenceGroupItem } from "utils/useReferencesTo";

// The raw `/references-to` group types now live in the neutral utils hook so the
// inline form-incoming-reference element can share them; re-exported here for the
// existing importers (this module + its test + ReferencesUsedByList).
export type { ReferenceGroup, ReferenceGroupItem };

/**
 * Pure transform behind the generic "Used by" (reverse-reference) tab.
 *
 * The `POST /references-to` endpoint returns one group per (referencing type,
 * column/attribute). For presentation (per Margot's review) those groups are
 * split into two sections:
 *   - every FK / custom-object REFERENCE group is flattened into ONE mixed list,
 *     each row carrying its referring type + the column/attribute it is
 *     referenced via, so a mixed set of referrers reads as a single standard
 *     resource list; and
 *   - the RUNTIME usage group(s) (flows that use this connector / wrapper /
 *     sub-flow when they run) are kept aside for a separate, clearly-labelled
 *     section, because "a flow ran this at runtime" is a different relationship
 *     from "a record points at this".
 *
 * Extracted from the component so the split logic is unit-testable in isolation
 * (no React / RecordTable / restClient dependency).
 */

export const RUNTIME_USAGE_KIND = "resource_usage";

export interface ReferenceRow {
    id: string;
    name?: string;
    record_type: string;
    referencing_type_label: string;
    via_label?: string;
}

export interface UsedBySections {
    referenceRows: ReferenceRow[];
    referencesTruncated: boolean;
    runtimeGroups: ReferenceGroup[];
}

export const isRuntimeUsage = (group: ReferenceGroup): boolean =>
    group.reference_kind === RUNTIME_USAGE_KIND;

/** "OBJECT_TEMPLATE_ATTRIBUTE" -> "Object template attribute". */
export const prettyRecordType = (recordType: string): string => {
    const words = recordType.toLowerCase().split("_");
    return words
        .map((word, index) =>
            index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
        )
        .join(" ");
};

/**
 * Named labels for references held by a join row (`<table>.<column>`), where
 * the column name alone does not say what the relationship is.
 */
const JOIN_COLUMN_LABELS: Record<string, string> = {
    "identity_role.role_id": "Role assignment",
    "resource_wrapper.wrapper_id": "Wrapper attachment",
};

/**
 * Plain-language label for the column a reference is held in:
 * "identity_role.role_id" -> "Role assignment", "schedule_id" -> "Schedule",
 * "reference_object_template_id" -> "Reference object template".
 */
export const prettyViaColumn = (viaColumn: string): string => {
    const named = JOIN_COLUMN_LABELS[viaColumn];
    if (named) {
        return named;
    }
    const column = viaColumn.split(".").pop() ?? viaColumn;
    const base = column.replace(/_id$/, "");
    return prettyRecordType(base || column);
};

export const buildUsedBySections = (
    groups: ReferenceGroup[] | null | undefined
): UsedBySections => {
    const all = groups ?? [];
    const refGroups = all.filter((group) => !isRuntimeUsage(group));
    const referenceRows: ReferenceRow[] = refGroups.flatMap((group) => {
        const feType = getTypedLowerCase(group.referencing_record_type);
        // A custom-object attribute already carries its human label; a core
        // column is turned into plain language.
        const via = group.via_column
            ? prettyViaColumn(group.via_column)
            : group.via_attribute;
        return group.items.map((item) => ({
            id: item.id,
            name: item.name ?? undefined,
            record_type: feType,
            referencing_type_label: prettyRecordType(group.referencing_record_type),
            via_label: via,
        }));
    });
    return {
        referenceRows,
        referencesTruncated: refGroups.some((group) => group.truncated),
        runtimeGroups: all.filter(isRuntimeUsage),
    };
};
